import React from 'react';
import PropTypes from 'prop-types';
import FilterSettingsContent from './FilterSettingsContent';
import AWSFilters from '../section/AWSFilters';

/**
 * Schema-aware wrapper around FilterSettingsContent that uses schema boolean
 * flags to control which filter options are visible by passing empty arrays
 * when disabled.
 */
const DynamicFilterConfig = ({ schema, ...props }) => {
  if (!schema) return null;

  // Known custom section components
  const customComponents = {
    AWSFilters: AWSFilters
  };

  const beforeSections = (schema.customSections || []).filter(s => s.position === 'before');
  const afterSections = (schema.customSections || []).filter(s => s.position === 'after');

  function renderCustomSection(section, index) {
    const Component = customComponents[section.component];
    if (!Component) {
      console.warn(`Unknown custom section component: ${section.component}`);
      return null;
    }
    // Pick only the props specified in the section definition
    const sectionProps = {};
    (section.props || []).forEach(propName => {
      if (props[propName] !== undefined) {
        sectionProps[propName] = props[propName];
      }
    });
    return <Component key={`custom-${index}`} isSubmitted={props.isSubmitted} {...sectionProps} />;
  }

  return (
    <>
      {beforeSections.map((s, i) => renderCustomSection(s, i))}
      <FilterSettingsContent
        vendorId={props.vendorId}
        vendorLabel={props.vendorLabel}
        isSubmitted={props.isSubmitted}
        bugsDateBack={props.bugsDateBack}
        setBugsDateBack={props.setBugsDateBack}
        isCve={schema.showCveFilter ? props.isCve : undefined}
        setIsCve={props.setIsCve}
        vendorStatusOptions={schema.showStatuses ? props.vendorStatusOptions || [] : []}
        vendorStatuses={props.vendorStatuses || []}
        setVendorStatuses={props.setVendorStatuses}
        vendorEventScopeOptions={schema.showEventScopes ? props.vendorEventScopeOptions || [] : []}
        vendorEventScopes={props.vendorEventScopes || []}
        setVendorEventScopes={schema.showEventScopes ? props.setVendorEventScopes : undefined}
        vendorSupportStatusOptions={schema.showSupportStatus ? props.vendorSupportStatusOptions || [] : []}
        vendorSupportStatus={props.vendorSupportStatus || []}
        setVendorSupportStatus={props.setVendorSupportStatus}
        vendorImpactedRegionOptions={schema.showImpactedRegions ? props.vendorImpactedRegionOptions || [] : []}
        vendorImpactedRegions={props.vendorImpactedRegions || []}
        setVendorImpactedRegions={props.setVendorImpactedRegions}
        requireImpactedRegions={props.requireImpactedRegions}
        vendorRiskCategoryOptions={schema.showRiskCategories ? props.vendorRiskCategoryOptions || [] : []}
        vendorRiskCategories={props.vendorRiskCategories || []}
        setVendorRiskCategories={props.setVendorRiskCategories}
        vendorMitigationTypeOptions={schema.showMitigationTypes ? props.vendorMitigationTypeOptions || [] : []}
        vendorMitigationTypes={props.vendorMitigationTypes || []}
        setVendorMitigationTypes={props.setVendorMitigationTypes}
        vendorBugTypeOptions={schema.showBugTypes ? props.vendorBugTypeOptions || [] : []}
        vendorBugTypes={props.vendorBugTypes || []}
        setVendorBugTypes={props.setVendorBugTypes}
        vendorComplianceStatusOptions={schema.showComplianceStatus ? props.vendorComplianceStatusOptions || [] : []}
        vendorComplianceStatus={props.vendorComplianceStatus || []}
        setVendorComplianceStatus={props.setVendorComplianceStatus}
        vendorBugCategoryOptions={schema.showBugCategories ? props.vendorBugCategoryOptions || [] : []}
        vendorBugCategories={props.vendorBugCategories || []}
        setVendorBugCategories={props.setVendorBugCategories}
        vendorResolutionOptions={schema.showResolutions ? props.vendorResolutionOptions || [] : []}
        vendorResolutions={props.vendorResolutions || []}
        setVendorResolutions={props.setVendorResolutions}
        vendorBugServiceOptions={schema.showBugServices ? props.vendorBugServiceOptions || [] : []}
        vendorBugServices={props.vendorBugServices || []}
        setVendorBugServices={props.setVendorBugServices}
        vendorProductBugCategoryOptions={
          schema.showProductBugCategories ? props.vendorProductBugCategoryOptions || [] : undefined
        }
        vendorProductBugCategories={props.vendorProductBugCategories || []}
        setVendorProductBugCategories={props.setVendorProductBugCategories}
        includedComponents={props.includedComponents || []}
        setIncludedComponents={props.setIncludedComponents}
        excludedComponents={props.excludedComponents || []}
        setExcludedComponents={props.setExcludedComponents}
        keywords={props.keywords || []}
        setKeywords={props.setKeywords}
        searchFields={props.searchFields || []}
        setSearchFields={props.setSearchFields}
        loader={props.loader}
        setLoader={props.setLoader}
        setQuery={props.setQuery}
        query={props.query}
        offset={props.offset}
        limit={props.limit}
      >
        {props.children}
      </FilterSettingsContent>
      {afterSections.map((s, i) => renderCustomSection(s, i))}
    </>
  );
};

// All FilterSettingsContent props are passed through via ...props spread.
// Only schema-specific and commonly referenced props are validated here.
DynamicFilterConfig.propTypes = {
  schema: PropTypes.object.isRequired,
  vendorId: PropTypes.string,
  vendorLabel: PropTypes.string,
  isSubmitted: PropTypes.bool,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  isCve: PropTypes.bool,
  setIsCve: PropTypes.func,
  vendorStatusOptions: PropTypes.array,
  vendorStatuses: PropTypes.array,
  setVendorStatuses: PropTypes.func,
  vendorEventScopeOptions: PropTypes.array,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorSupportStatusOptions: PropTypes.array,
  vendorSupportStatus: PropTypes.array,
  setVendorSupportStatus: PropTypes.func,
  vendorImpactedRegionOptions: PropTypes.array,
  vendorImpactedRegions: PropTypes.array,
  setVendorImpactedRegions: PropTypes.func,
  requireImpactedRegions: PropTypes.bool,
  vendorRiskCategoryOptions: PropTypes.array,
  vendorRiskCategories: PropTypes.array,
  setVendorRiskCategories: PropTypes.func,
  vendorMitigationTypeOptions: PropTypes.array,
  vendorMitigationTypes: PropTypes.array,
  setVendorMitigationTypes: PropTypes.func,
  vendorBugTypeOptions: PropTypes.array,
  vendorBugTypes: PropTypes.array,
  setVendorBugTypes: PropTypes.func,
  vendorComplianceStatusOptions: PropTypes.array,
  vendorComplianceStatus: PropTypes.array,
  setVendorComplianceStatus: PropTypes.func,
  vendorBugCategoryOptions: PropTypes.array,
  vendorBugCategories: PropTypes.array,
  setVendorBugCategories: PropTypes.func,
  vendorResolutionOptions: PropTypes.array,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  vendorBugServiceOptions: PropTypes.array,
  vendorBugServices: PropTypes.array,
  setVendorBugServices: PropTypes.func,
  vendorProductBugCategoryOptions: PropTypes.array,
  vendorProductBugCategories: PropTypes.array,
  setVendorProductBugCategories: PropTypes.func,
  includedComponents: PropTypes.array,
  setIncludedComponents: PropTypes.func,
  excludedComponents: PropTypes.array,
  setExcludedComponents: PropTypes.func,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  loader: PropTypes.bool,
  setLoader: PropTypes.func,
  setQuery: PropTypes.func,
  query: PropTypes.string,
  offset: PropTypes.string,
  limit: PropTypes.string,
  children: PropTypes.node
};

export default DynamicFilterConfig;
